import type { UseQueryResult } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import BandwidthHeatmapCountsTable, { type HeatmapCountsByCustomerRow } from './BandwidthHeatmapCountsTable';
import BandwidthHeatmapTable from './BandwidthHeatmapTable';
import BandwidthTimeByGroupBar from './BandwidthTimeByGroupBar';
import type { TimeByGroupBarRow } from './bandwidthMockData';
import { buildHeatmapMatrix } from './bandwidthHeatmapMatrix';
import { BANDWIDTH_UNALLOCATED_TEAM_ID, type BandwidthDashboardData } from './bandwidthDashboardTypes';
import {
  BW_ALL_TASK_TYPES_FILTER_VALUE,
  resolveBandwidthEffectiveExportDateRange,
} from './bandwidthDashboardApi';
import type { BandwidthClassicDrillDown } from './bandwidthClassicDrill';
import { useBandwidthDashboard } from './useBandwidthDashboard';
import { SectionLabel } from '../kpi-dashboard/KpiDashboardContent';

export interface BandwidthClassicDashboardGridProps {
  filterKey: string;
  filterValues: Record<string, string | string[]>;
  customers: Array<{ id: string; accountName: string }>;
  assignmentGroups: Array<{ id: string; name: string }>;
  dashboardQuery: UseQueryResult<BandwidthDashboardData, Error>;
  drillDown: BandwidthClassicDrillDown;
  onDrillDownChange: React.Dispatch<React.SetStateAction<BandwidthClassicDrillDown>>;
  /** Syncs page Teams filter so bar/column clicks scope all widgets (customer heatmap, counts, team charts). */
  onAssignmentGroupChange?: (groupId: string) => void;
  /** Syncs page Task Types when a team×type heatmap cell is selected (with Teams). */
  onTimeEntryTypesChange?: (types: string[]) => void;
}

function lookupActivityType(
  rows: BandwidthDashboardData['typeByCustomer'] | undefined,
  rowLabel: string,
  colLabel: string
): string | undefined {
  return rows?.find((r) => r.activityTypeLabel === rowLabel && r.customer === colLabel)?.activityType;
}

function lookupCustomerId(
  customersList: Array<{ id: string; accountName: string }>,
  accountName: string
): string | undefined {
  return customersList.find((c) => c.accountName === accountName)?.id;
}

/** Resolve drill from a group×type heatmap cell (prefers API `assignmentGroupId`, then filter options name match). */
function resolveGroupHeatmapDrill(
  rows: BandwidthDashboardData['typeByAssignmentGroup'] | undefined,
  groupsList: Array<{ id: string; name: string }>,
  rowLabel: string,
  colLabel: string
): { activityType: string; groupId: string } | undefined {
  const hit = rows?.find((r) => r.activityTypeLabel === rowLabel && r.assignmentGroup === colLabel);
  const activityType = hit?.activityType;
  const gid = hit?.assignmentGroupId?.trim();
  const groupId = gid || lookupGroupId(groupsList, colLabel);
  if (!activityType || !groupId) return undefined;
  return { activityType, groupId };
}

function lookupGroupId(
  groups: Array<{ id: string; name: string }>,
  groupName: string
): string | undefined {
  return groups.find((g) => g.name === groupName)?.id;
}

const BandwidthClassicDashboardGrid: React.FC<BandwidthClassicDashboardGridProps> = ({
  filterKey,
  filterValues,
  customers,
  assignmentGroups,
  dashboardQuery,
  drillDown,
  onDrillDownChange,
  onAssignmentGroupChange,
  onTimeEntryTypesChange,
}) => {
  const drillSpec = useMemo(() => {
    if (!drillDown) return null;
    switch (drillDown.kind) {
      case 'heatmap':
        return {
          key: `hm:${drillDown.customerId}:${drillDown.activityType}`,
          filterValues: {
            ...filterValues,
            customer: drillDown.customerId,
            timeEntryType: [drillDown.activityType],
          },
          applyAssignmentGroupFromFilters: false,
        };
      case 'customer':
        return {
          key: `cust:${drillDown.customerId}`,
          filterValues: { ...filterValues, customer: drillDown.customerId },
          applyAssignmentGroupFromFilters: false,
        };
      case 'typeRow':
        return {
          key: `tr:${drillDown.activityType}`,
          filterValues: {
            ...filterValues,
            timeEntryType: [drillDown.activityType],
          },
          applyAssignmentGroupFromFilters: false,
        };
      case 'groupHeatmap':
        return {
          key: `ghm:${drillDown.groupId}:${drillDown.activityType}`,
          filterValues: {
            ...filterValues,
            assignmentGroup: drillDown.groupId,
            timeEntryType: [drillDown.activityType],
          },
          applyAssignmentGroupFromFilters: true,
        };
      case 'groupBar':
        return {
          key: `gb:${drillDown.groupId}`,
          filterValues: {
            ...filterValues,
            assignmentGroup: drillDown.groupId,
            timeEntryType: [],
          },
          applyAssignmentGroupFromFilters: true,
        };
      default:
        return null;
    }
  }, [drillDown, filterValues]);

  const classicDrillQuery = useBandwidthDashboard(
    `${filterKey}__${drillSpec?.key ?? 'none'}`,
    drillSpec?.filterValues ?? filterValues,
    {
      enabled: drillSpec != null,
      queryKeyExtra: 'classic-tab-drill',
      applyCountTimePageFilters: false,
      applyAssignmentGroupFromFilters: drillSpec?.applyAssignmentGroupFromFilters ?? false,
      applyAssigneePageFilters: true,
    }
  );

  const baseData = dashboardQuery.data;
  const drillData = classicDrillQuery.data;

  const { dateFrom: exportDateFrom, dateTo: exportDateTo } = useMemo(
    () => resolveBandwidthEffectiveExportDateRange(filterValues, baseData),
    [filterValues, baseData]
  );

  /** Customer × type heatmap: full grid when drilling a single cell; otherwise reflect drill scope. */
  const typeByCustomerSource =
    drillDown?.kind === 'heatmap' ? baseData : drillSpec != null ? drillData ?? baseData : baseData;

  const countsSource = drillSpec != null ? drillData ?? baseData : baseData;

  /** Group × type heatmap: keep page-level matrix when the active drill is a cell in this chart. */
  const typeByGroupSource =
    drillDown?.kind === 'groupHeatmap' ? baseData : drillSpec != null ? drillData ?? baseData : baseData;

  const barDataSource = drillSpec != null ? drillData ?? baseData : baseData;

  const handleTypeByCustomerCellClick = useCallback(
    (rowLabel: string, colLabel: string) => {
      const customerId = lookupCustomerId(customers, colLabel);
      const activityType = lookupActivityType(baseData?.typeByCustomer, rowLabel, colLabel);
      if (!customerId || !activityType) return;

      onDrillDownChange((prev) => {
        if (
          prev?.kind === 'heatmap' &&
          prev.customerId === customerId &&
          prev.activityType === activityType
        ) {
          return null;
        }
        return { kind: 'heatmap', activityType, customerId, rowLabel, colLabel };
      });
    },
    [customers, baseData?.typeByCustomer, onDrillDownChange]
  );

  const handleCountsRowClick = useCallback(
    (row: HeatmapCountsByCustomerRow) => {
      const customerId = row.customerId || lookupCustomerId(customers, row.customer);
      if (!customerId) return;
      onDrillDownChange((prev) => {
        if (prev?.kind === 'customer' && prev.customerId === customerId) {
          return null;
        }
        return { kind: 'customer', customerId };
      });
    },
    [customers, onDrillDownChange]
  );

  const handleCustomerHeatmapTypeRow = useCallback(
    (rowLabel: string) => {
      const activityType = baseData?.typeByCustomer?.find((r) => r.activityTypeLabel === rowLabel)?.activityType;
      if (!activityType) return;
      onDrillDownChange((prev) => {
        if (prev?.kind === 'typeRow' && prev.activityType === activityType) {
          return null;
        }
        return { kind: 'typeRow', activityType, rowLabel };
      });
    },
    [baseData?.typeByCustomer, onDrillDownChange]
  );

  const handleTeamsHeatmapTypeRow = useCallback(
    (rowLabel: string) => {
      const activityType = typeByGroupSource?.typeByAssignmentGroup?.find(
        (r) => r.activityTypeLabel === rowLabel
      )?.activityType;
      if (!activityType) return;
      onDrillDownChange((prev) => {
        if (prev?.kind === 'typeRow' && prev.activityType === activityType) {
          return null;
        }
        return { kind: 'typeRow', activityType, rowLabel };
      });
    },
    [typeByGroupSource?.typeByAssignmentGroup, onDrillDownChange]
  );

  const handleCustomerHeatmapColumnClick = useCallback(
    (colLabel: string) => {
      const customerId = lookupCustomerId(customers, colLabel);
      if (!customerId) return;
      onDrillDownChange((prev) => {
        if (prev?.kind === 'customer' && prev.customerId === customerId) {
          return null;
        }
        return { kind: 'customer', customerId };
      });
    },
    [customers, onDrillDownChange]
  );

  const handleTeamsHeatmapColumnClick = useCallback(
    (colLabel: string) => {
      const hit = typeByGroupSource?.typeByAssignmentGroup?.find((r) => r.assignmentGroup === colLabel);
      const gid = hit?.assignmentGroupId?.trim();
      const groupId =
        gid && gid !== BANDWIDTH_UNALLOCATED_TEAM_ID ? gid : lookupGroupId(assignmentGroups, colLabel);
      if (!groupId || groupId === BANDWIDTH_UNALLOCATED_TEAM_ID) return;
      const pageAg = String(filterValues.assignmentGroup ?? 'All');
      if (pageAg === groupId) {
        onAssignmentGroupChange?.('All');
      } else {
        onAssignmentGroupChange?.(groupId);
      }
      onDrillDownChange(null);
    },
    [
      assignmentGroups,
      typeByGroupSource?.typeByAssignmentGroup,
      filterValues.assignmentGroup,
      onAssignmentGroupChange,
      onDrillDownChange,
    ]
  );

  const handleGroupHeatmapCellClick = useCallback(
    (rowLabel: string, colLabel: string) => {
      const resolved = resolveGroupHeatmapDrill(
        typeByGroupSource?.typeByAssignmentGroup,
        assignmentGroups,
        rowLabel,
        colLabel
      );
      if (!resolved) return;
      const { groupId, activityType } = resolved;
      if (groupId === BANDWIDTH_UNALLOCATED_TEAM_ID) return;

      const pageAg = String(filterValues.assignmentGroup ?? 'All').trim() || 'All';
      const rawTypes = filterValues.timeEntryType;
      const typesArr = Array.isArray(rawTypes) ? rawTypes : rawTypes ? [String(rawTypes)] : [];
      const matchesThisCell =
        pageAg === groupId &&
        typesArr.length === 1 &&
        typesArr[0] === activityType &&
        typesArr[0] !== BW_ALL_TASK_TYPES_FILTER_VALUE;

      if (matchesThisCell) {
        onAssignmentGroupChange?.('All');
        onTimeEntryTypesChange?.([BW_ALL_TASK_TYPES_FILTER_VALUE]);
        onDrillDownChange(null);
        return;
      }

      onAssignmentGroupChange?.(groupId);
      onTimeEntryTypesChange?.([activityType]);
      onDrillDownChange(null);
    },
    [
      assignmentGroups,
      typeByGroupSource?.typeByAssignmentGroup,
      filterValues.assignmentGroup,
      filterValues.timeEntryType,
      onAssignmentGroupChange,
      onTimeEntryTypesChange,
      onDrillDownChange,
    ]
  );

  const handleGroupBarClick = useCallback(
    (row: TimeByGroupBarRow) => {
      const groupId = (row.groupId && row.groupId.trim()) || lookupGroupId(assignmentGroups, row.label);
      if (!groupId || groupId === BANDWIDTH_UNALLOCATED_TEAM_ID) return;
      const pageAg = String(filterValues.assignmentGroup ?? 'All');
      if (pageAg === groupId) {
        onAssignmentGroupChange?.('All');
      } else {
        onAssignmentGroupChange?.(groupId);
      }
      onDrillDownChange(null);
    },
    [assignmentGroups, filterValues.assignmentGroup, onAssignmentGroupChange, onDrillDownChange]
  );

  const typeByCustomerHm = useMemo(() => {
    if (!typeByCustomerSource?.typeByCustomer?.length) {
      return { rowLabels: [] as string[], columnLabels: [] as string[], cells: [] as number[][] };
    }
    return buildHeatmapMatrix(
      typeByCustomerSource.typeByCustomer
        .filter((r) => !r.reducesCapacity)
        .map((r) => ({
          rowKey: r.activityTypeLabel,
          colKey: r.customer,
          value: r.totalMinutes,
        }))
    );
  }, [typeByCustomerSource?.typeByCustomer]);

  const typeByGroupHm = useMemo(() => {
    if (!typeByGroupSource?.typeByAssignmentGroup?.length) {
      return { rowLabels: [] as string[], columnLabels: [] as string[], cells: [] as number[][] };
    }
    return buildHeatmapMatrix(
      typeByGroupSource.typeByAssignmentGroup.map((r) => ({
        rowKey: r.activityTypeLabel,
        colKey: r.assignmentGroup,
        value: r.totalMinutes,
      }))
    );
  }, [typeByGroupSource?.typeByAssignmentGroup]);

  const countRows: HeatmapCountsByCustomerRow[] = useMemo(() => {
    if (!countsSource?.countsByCustomer) return [];
    return countsSource.countsByCustomer.map((r) => ({
      customer: r.customer,
      customerId: r.customerId || lookupCustomerId(customers, r.customer),
      totalActivity: r.totalActivityCount,
      timeMinutes: r.timeSpentMinutes,
      timeHrs: r.timeSpentHours,
      avgHrsOnActivity: r.avgHrsOnActivity,
    }));
  }, [countsSource?.countsByCustomer, customers]);

  const barRows = useMemo(() => {
    if (!barDataSource?.timeByAssignmentGroup) return [];
    return barDataSource.timeByAssignmentGroup.map((r) => ({
      label: r.assignmentGroup,
      hours: r.totalHours,
      groupId: r.assignmentGroupId,
    }));
  }, [barDataSource?.timeByAssignmentGroup]);

  const selectedTeamBarLabel = useMemo(() => {
    if (drillDown?.kind === 'groupBar') return drillDown.groupLabel;
    if (drillDown?.kind === 'groupHeatmap') return drillDown.colLabel;
    const ag = typeof filterValues.assignmentGroup === 'string' ? filterValues.assignmentGroup : 'All';
    if (!ag || ag === 'All') return null;
    return assignmentGroups.find((g) => g.id === ag)?.name ?? barRows.find((r) => r.groupId === ag)?.label ?? null;
  }, [drillDown, filterValues.assignmentGroup, assignmentGroups, barRows]);

  const drillPending = drillSpec != null && classicDrillQuery.isPending && !classicDrillQuery.data;
  const drillErr =
    drillSpec != null && classicDrillQuery.isError
      ? classicDrillQuery.error instanceof Error
        ? classicDrillQuery.error.message
        : 'Could not load chart data.'
      : null;

  const countsDrillPending = drillSpec != null && drillPending;
  const countsDrillErr = drillSpec != null ? drillErr : null;

  const { isLoading: baseLoading, isError: baseIsError, error: baseError } = dashboardQuery;

  if (baseLoading && !baseData) {
    return (
      <div className="flex min-h-[280px] items-center justify-center rounded-2xl border border-indigo-100/80 bg-white/90 p-12 text-sm text-slate-600 shadow-sm shadow-indigo-900/5 ring-1 ring-slate-950/[0.03]">
        Loading bandwidth dashboard…
      </div>
    );
  }

  if (baseIsError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50/90 p-6 text-sm text-red-800 shadow-sm">
        {baseError instanceof Error ? baseError.message : 'Could not load bandwidth dashboard.'}
      </div>
    );
  }

  const customerHeatmapSelection =
    drillDown?.kind === 'heatmap'
      ? { row: drillDown.rowLabel, col: drillDown.colLabel }
      : drillDown?.kind === 'typeRow'
        ? { row: drillDown.rowLabel, col: null as string | null }
        : { row: null as string | null, col: null as string | null };

  const pageTeamAg = String(filterValues.assignmentGroup ?? 'All').trim() || 'All';
  const rawPageTypes = filterValues.timeEntryType;
  const pageTypesArr = Array.isArray(rawPageTypes)
    ? rawPageTypes
    : rawPageTypes
      ? [String(rawPageTypes)]
      : [];
  const pageTeamTypeCellSelection =
    pageTeamAg !== 'All' &&
    pageTeamAg !== '' &&
    pageTypesArr.length === 1 &&
    pageTypesArr[0] !== BW_ALL_TASK_TYPES_FILTER_VALUE &&
    baseData
      ? (() => {
          const at = pageTypesArr[0];
          const teamName = assignmentGroups.find((g) => g.id === pageTeamAg)?.name;
          const rowLbl = baseData.typeByAssignmentGroup?.find((r) => r.activityType === at)?.activityTypeLabel;
          if (!teamName || !rowLbl) return null;
          return { row: rowLbl, col: teamName };
        })()
      : null;

  const groupHeatmapSelection =
    drillDown?.kind === 'groupHeatmap'
      ? { row: drillDown.rowLabel, col: drillDown.colLabel }
      : drillDown?.kind === 'typeRow'
        ? { row: drillDown.rowLabel, col: null as string | null }
        : (pageTeamTypeCellSelection ?? { row: null as string | null, col: null as string | null });

  const selectedCountsCustomerId =
    drillDown?.kind === 'heatmap' || drillDown?.kind === 'customer' ? drillDown.customerId : null;

  const groupEmptyHint = drillErr ?? (drillPending ? 'Loading…' : 'No data for the selected filters.');

  return (
    <section className="mb-6 w-full" aria-labelledby="tasks-customers-activity-kpis-heading">
      <SectionLabel id="tasks-customers-activity-kpis-heading">Tasks & customers activity KPIs</SectionLabel>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
        <BandwidthHeatmapTable
          title="Tasks Type By Customer"
          rowLabelHeader="Type"
          rowLabels={typeByCustomerHm.rowLabels}
          columnLabels={typeByCustomerHm.columnLabels}
          cells={typeByCustomerHm.cells}
          valueUnitToggle
          interactive
          selectedRowLabel={customerHeatmapSelection.row}
          selectedColLabel={customerHeatmapSelection.col}
          onCellClick={handleTypeByCustomerCellClick}
          onRowLabelClick={handleCustomerHeatmapTypeRow}
          onColumnLabelClick={handleCustomerHeatmapColumnClick}
          exportMatrix={{
            filePrefix: 'bandwidth_tasks_type_by_customer',
            sheetName: 'Type by customer',
            dateFrom: exportDateFrom,
            dateTo: exportDateTo,
          }}
        />
        <BandwidthHeatmapCountsTable
          title="Counts & Time By Customer"
          rows={countRows}
          selectedCustomerId={selectedCountsCustomerId}
          onRowClick={handleCountsRowClick}
          loading={countsDrillPending}
          errorMessage={countsDrillErr}
          exportFilePrefix="bandwidth_counts_time_by_customer"
          exportDateFrom={exportDateFrom}
          exportDateTo={exportDateTo}
        />
        <BandwidthHeatmapTable
          title="Tasks Type By Teams"
          rowLabelHeader="Type"
          rowLabels={typeByGroupHm.rowLabels}
          columnLabels={typeByGroupHm.columnLabels}
          cells={typeByGroupHm.cells}
          valueUnitToggle
          interactive
          selectedRowLabel={groupHeatmapSelection.row}
          selectedColLabel={groupHeatmapSelection.col}
          onCellClick={handleGroupHeatmapCellClick}
          onRowLabelClick={handleTeamsHeatmapTypeRow}
          onColumnLabelClick={handleTeamsHeatmapColumnClick}
          emptyMessage={groupEmptyHint}
          exportMatrix={{
            filePrefix: 'bandwidth_tasks_type_by_teams',
            sheetName: 'Type by team',
            dateFrom: exportDateFrom,
            dateTo: exportDateTo,
          }}
        />
        <BandwidthTimeByGroupBar
          title="Time (Hrs) by Team"
          rows={barRows}
          emptyHint={barRows.length === 0 ? groupEmptyHint : undefined}
          interactive
          selectedLabel={selectedTeamBarLabel}
          onBarClick={handleGroupBarClick}
        />
      </div>
    </section>
  );
};

export default BandwidthClassicDashboardGrid;
